import type { ConfigDTO, VenueDTO } from '../../../types'
import type { ColumnDef } from '../../DataTable/DataTable'
import { formatDate } from '../../../helpers/formatDate'
import { deriveConfigScope } from '../../../helpers/deriveConfigScope'
import { CodeBadge } from '../../CodeBadge/CodeBadge'
import { Badge } from '../../Badge/Badge'

export function buildConfigsTableColumns(
  venues: readonly VenueDTO[]
): ColumnDef<ConfigDTO>[] {
  const venueById = new Map(venues.map((venue) => [venue.id, venue]))
  const formatConfigVenueLabel = (venueId: string | null) => {
    if (!venueId) {
      return null
    }
    const venue = venueById.get(venueId)
    return venue ? `${venueId} (${venue.venueSlug})` : venueId
  }

  return [
    {
      header: 'ID',
      sortValue: (c) => c.id,
      accessor: (c) => c.id,
      className: 'table-id-col text-end',
      cellClassName: 'font-monospace small',
    },
    {
      header: 'Scope',
      sortValue: (c) => deriveConfigScope(c),
      accessor: (c) => {
        const scope = deriveConfigScope(c)
        return scope ? <Badge color="blue">{scope}</Badge> : null
      },
    },
    {
      header: 'Tenant',
      sortValue: (c) => c.tenantCode,
      accessor: (c) => <CodeBadge code={c.tenantCode} />,
    },
    {
      header: 'ISP',
      sortValue: (c) => c.inventorySystemProfileCode,
      accessor: (c) => <CodeBadge code={c.inventorySystemProfileCode} />,
    },
    {
      header: 'Venue',
      sortValue: (c) => formatConfigVenueLabel(c.venueId),
      accessor: (c) => <CodeBadge code={formatConfigVenueLabel(c.venueId)} />,
    },
    {
      header: 'Key',
      sortValue: (c) => c.configKey,
      accessor: (c) => c.configKey,
      cellClassName: 'fw-medium',
    },
    {
      header: 'Value',
      sortValue: (c) => c.configValue,
      accessor: (c) => c.configValue,
    },
    {
      header: 'Type',
      sortValue: (c) => c.configType,
      accessor: (c) => <Badge color="amber">{c.configType}</Badge>,
    },
    {
      header: 'Tags',
      sortValue: (c) =>
        Object.entries(c.tags)
          .map(([key, value]) => `${key}:${value}`)
          .join(', '),
      accessor: (c) => {
        const entries = Object.entries(c.tags)
        if (entries.length === 0) {
          return null
        }
        return (
          <div className="configs-table-tags">
            {entries.map(([key, value]) => (
              <Badge key={key} color="grey">
                {key}:{value}
              </Badge>
            ))}
          </div>
        )
      },
    },
    {
      header: 'Created',
      sortValue: (c) => c.createdAt,
      accessor: (c) => formatDate(c.createdAt),
    },
    {
      header: 'Updated',
      sortValue: (c) => c.updatedAt,
      accessor: (c) => formatDate(c.updatedAt),
    },
  ]
}
